import type { Query } from "../../api/client";
import type { Metal, OwnershipStatus, PieceKind, StoneType } from "../../api/types";

/**
 * Filter state lives in the URL. That is what makes Telegram's back button, our
 * own history, and a re-opened deep link all agree without a store to sync.
 */
export interface Filters {
  q: string;
  kind: PieceKind[];
  metal: Metal[];
  ownership_status: OwnershipStatus[];
  purity: number | null;
  stone: StoneType | null;
  has_stones: boolean | null;
}

export const emptyFilters: Filters = {
  q: "",
  kind: [],
  metal: [],
  ownership_status: [],
  purity: null,
  stone: null,
  has_stones: null,
};

const MULTI = ["kind", "metal", "ownership_status"] as const;

export function filtersFromParams(params: URLSearchParams): Filters {
  const raw = params.get("purity");
  const purity = raw === null ? Number.NaN : Number(raw);
  const hasStones = params.get("has_stones");
  return {
    q: params.get("q") ?? "",
    kind: params.getAll("kind").filter(Boolean) as PieceKind[],
    metal: params.getAll("metal").filter(Boolean) as Metal[],
    ownership_status: params.getAll("ownership_status").filter(Boolean) as OwnershipStatus[],
    purity: Number.isFinite(purity) ? purity : null,
    stone: (params.get("stone") || null) as StoneType | null,
    has_stones: hasStones === null ? null : hasStones === "true",
  };
}

export function filtersToParams(filters: Filters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.q) params.set("q", filters.q);
  for (const key of MULTI) for (const value of filters[key]) params.append(key, value);
  if (filters.purity !== null) params.set("purity", String(filters.purity));
  if (filters.stone) params.set("stone", filters.stone);
  if (filters.has_stones !== null) params.set("has_stones", String(filters.has_stones));
  return params;
}

/** Exactly the query keys jewelry/filters.py accepts. */
export function filtersToQuery(filters: Filters): Query {
  return {
    q: filters.q || undefined,
    kind: filters.kind.length ? filters.kind : undefined,
    metal: filters.metal.length ? filters.metal : undefined,
    ownership_status: filters.ownership_status.length ? filters.ownership_status : undefined,
    purity: filters.purity ?? undefined,
    stone: filters.stone ?? undefined,
    has_stones: filters.has_stones ?? undefined,
  };
}

/** How many chips the filter button should badge. `q` is the search box, not a filter. */
export function activeFilterCount(filters: Filters): number {
  let count = 0;
  for (const key of MULTI) count += filters[key].length;
  if (filters.purity !== null) count += 1;
  if (filters.stone) count += 1;
  if (filters.has_stones !== null) count += 1;
  return count;
}

export function hasAnyFilter(filters: Filters): boolean {
  return activeFilterCount(filters) > 0 || filters.q.length > 0;
}
